import { supabase } from '../lib/supabaseClient';
import type { Material, MaterialSortKey, MaterialFilter } from '../types/materials';

/**
 * アクティブな単元ガイド一覧を取得
 */
export async function fetchMaterials(): Promise<Material[]> {
  try {
    console.log('📚 単元ガイド一覧を取得中...');
    
    const { data, error } = await supabase
      .from('materials')
      .select('id, slug, title, subject, grade_min, grade_max, tags, description, is_featured')
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ 単元ガイド取得エラー:', error.message);
      console.error('エラー詳細:', {
        code: error.code,
        details: error.details,
        hint: error.hint,
      });
      // エラー時は空配列を返す（画面を壊さない）
      return [];
    }

    console.log('✅ 単元ガイド取得成功:', data?.length || 0, '件');
    return data || [];
  } catch (error) {
    console.error('❌ 単元ガイド取得例外:', error);
    // 例外時も空配列を返す（画面を壊さない）
    return [];
  }
}

/**
 * 単元ガイド一覧を取得（ソート・絞り込み対応）
 */
export async function fetchMaterialsList(options?: {
  sort?: MaterialSortKey;
  filter?: MaterialFilter;
  limit?: number;
  offset?: number;
}) {
  const { sort = 'recommended', filter, limit = 50, offset = 0 } = options ?? {};

  let query = supabase
    .from('materials')
    .select('*')
    .eq('is_active', true)
    .range(offset, offset + limit - 1);

  // ========================================
  // フィルタの適用
  // ========================================

  // 教科
  if (filter?.subjects && filter.subjects.length > 0) {
    query = query.in('subject', filter.subjects);
  }

  // 学年範囲
  if (filter?.gradeRange?.min != null) {
    // grade_max が指定された最小学年以上
    query = query.gte('grade_max', filter.gradeRange.min);
  }
  if (filter?.gradeRange?.max != null) {
    // grade_min が指定された最大学年以下
    query = query.lte('grade_min', filter.gradeRange.max);
  }

  // タグ（ARRAY 型なので overlaps を利用）
  if (filter?.tags && filter.tags.length > 0) {
    query = query.overlaps('tags', filter.tags);
  }

  // ========================================
  // ソート
  // ========================================

  if (sort === 'newest' || sort === 'recommended') {
    // 新着順 / おすすめ（まずは新着順でソート）
    query = query.order('created_at', { ascending: false });
  } else if (sort === 'title_asc') {
    // タイトルのあいうえお順
    query = query.order('title', { ascending: true });
  } else if (sort === 'grade_low_high') {
    // 学年の低い順
    query = query.order('grade_min', { ascending: true });
  } else if (sort === 'grade_high_low') {
    // 学年の高い順
    query = query.order('grade_min', { ascending: false });
  }

  const { data, error } = await query;
  if (error) {
    console.error('fetchMaterialsList error', error);
    throw error;
  }

  let results = data ?? [];

  // ========================================
  // クライアント側でのソート（おすすめ順）
  // ========================================

  if (sort === 'recommended') {
    // おすすめ順：is_featured を優先、次に新しさ
    results = results.sort((a, b) => {
      // is_featured が true の方を優先
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;

      // 同じなら新しい方を優先
      const dateA = new Date(a.created_at ?? 0).getTime();
      const dateB = new Date(b.created_at ?? 0).getTime();
      return dateB - dateA;
    });
  }

  return results;
}

/**
 * slugで単元ガイドを1件取得
 */
export async function fetchMaterialBySlug(slug: string): Promise<Material | null> {
  try {
    console.log('📖 単元ガイド詳細を取得中:', slug);
    
    const { data, error } = await supabase
      .from('materials')
      .select('*')
      .eq('slug', slug)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        console.log('ℹ️ 単元ガイドが見つかりません:', slug);
        return null;
      }
      console.error('❌ 単元ガイド詳細取得エラー:', error.message);
      return null;
    }

    console.log('✅ 単元ガイド詳細取得成功:', data.title);
    return data;
  } catch (error) {
    console.error('❌ 単元ガイド詳細取得例外:', error);
    return null;
  }
}

/**
 * 注目の単元ガイドを1件取得（トップページ用）
 */
export async function fetchFeaturedMaterial(): Promise<Material | null> {
  try {
    console.log('⭐ 注目の単元ガイドを取得中...');
    
    // まずis_featured = trueのものを探す
    const { data: featuredData, error: featuredError } = await supabase
      .from('materials')
      .select('id, slug, title, subject, grade_min, grade_max, tags, description, is_featured')
      .eq('is_active', true)
      .eq('is_featured', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (!featuredError && featuredData) {
      console.log('✅ 注目の単元ガイド取得成功:', featuredData.title);
      return featuredData;
    }

    // is_featured = trueがない場合は最新1件を取得
    const { data, error } = await supabase
      .from('materials')
      .select('id, slug, title, subject, grade_min, grade_max, tags, description, is_featured')
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        console.log('ℹ️ 単元ガイドがまだありません');
        return null;
      }
      console.error('❌ 注目の単元ガイド取得エラー:', error.message);
      return null;
    }

    console.log('✅ 最新の単元ガイド取得成功:', data.title);
    return data;
  } catch (error) {
    console.error('❌ 注目の単元ガイド取得例外:', error);
    return null;
  }
}

/**
 * レシピ投稿フォーム用の単元ガイド一覧を取得
 */
export async function fetchMaterialsForRecipeSelect() {
  const { data, error } = await supabase
    .from('materials')
    .select('id, title, subject, grade_min, grade_max, is_active')
    .eq('is_active', true)
    .order('subject', { ascending: true })
    .order('grade_min', { ascending: true });

  if (error) {
    console.error('fetchMaterialsForRecipeSelect error', error);
    throw error;
  }

  return data ?? [];
}

/**
 * レシピに紐づく単元ガイドの情報をIDから取得
 */
export async function fetchMaterialForRecipeById(id: string) {
  const { data, error } = await supabase
    .from('materials')
    .select('id, slug, title, subject, grade_min, grade_max')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('fetchMaterialForRecipeById error', error);
    throw error;
  }

  return data;
}

/**
 * 管理者用：全ての単元ガイドを取得（is_activeに関わらず）
 */
export async function fetchAllMaterialsForAdmin() {
  const { data, error } = await supabase
    .from('materials')
    .select(`
      *,
      material_site_links(count)
    `)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('fetchAllMaterialsForAdmin error', error);
    throw error;
  }

  // おすすめサイト数を追加
  return (data ?? []).map((material: any) => ({
    ...material,
    recommendedSitesCount: material.material_site_links?.[0]?.count || 0,
  }));
}

/**
 * 管理者用：単元ガイドを新規作成
 */
export async function createMaterial(payload: {
  slug: string;
  title: string;
  subject: string;
  grade_min: number;
  grade_max: number;
  description: string;
  tags?: string[];
  is_featured?: boolean;
  is_active?: boolean;
  recommended_points?: string;
  recommended_links?: string[];
}) {
  const { data, error } = await supabase
    .from('materials')
    .insert({
      ...payload,
      tags: payload.tags || [],
      is_featured: payload.is_featured ?? false,
      is_active: payload.is_active ?? true,
    })
    .select()
    .single();

  if (error) {
    console.error('createMaterial error', error);
    throw error;
  }

  return data;
}

/**
 * 管理者用：単元ガイドを更新
 */
export async function updateMaterial(
  id: string,
  payload: Partial<{
    slug: string;
    title: string;
    subject: string;
    grade_min: number;
    grade_max: number;
    description: string;
    tags: string[];
    is_featured: boolean;
    is_active: boolean;
    recommended_points: string;
    recommended_links: string[];
  }>
) {
  const { data, error } = await supabase
    .from('materials')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('updateMaterial error', error);
    throw error;
  }

  return data;
}

/**
 * 管理者用：単元ガイドを削除
 */
export async function deleteMaterial(id: string) {
  const { error } = await supabase
    .from('materials')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('deleteMaterial error', error);
    throw error;
  }
}

/**
 * 管理者用：全ての単元ガイドを取得（is_activeに関わらず）
 * fetchAllMaterialsForAdmin のエイリアス
 */
export async function fetchAllMaterials() {
  return fetchAllMaterialsForAdmin();
}

/**
 * 管理者用：単元ガイドのステータスを更新
 */
export async function updateMaterialStatus(id: string, status: 'draft' | 'published' | 'archived') {
  const isActive = status === 'published';
  
  const { data, error } = await supabase
    .from('materials')
    .update({ is_active: isActive })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('updateMaterialStatus error', error);
    throw error;
  }

  return data;
}
